import { useCallback, useState } from "react";

import { useRevertShowMoreChildren, useVisibleChildrenCount } from "../VisibleElementsCountProvider";
import { FilterQueryOptions } from "../../TreePicklistItemProvider";
import { FilterBuilder } from "../../../Common/FilterBuilder";
import { useAsyncEffect } from "../../../../../../../../Common/UseAsyncEffect";
import { isNotNullOrUndefined } from "../../../../../../../../Common/TypingUtils";
import { TreePicklistItem } from "../../TreePicklistItem";
import { useTreePicklistItemFabric } from "../../TreePicklistModal/useTreePicklistItemFabric";

import { RequestNodesCallback, useNodesResponse, UseRequestNodesEffect } from "./useNodesResponse";
import { CommonTreeNodeProps } from "./ITreeNodeProps";

interface UseUpdateNodesParams {
    props: CommonTreeNodeProps;
    summaryOpened: boolean;
    nodesNeedToOpen: TreePicklistItem[];
    searchMode: boolean;
    onLoadingError: () => void;
}

interface UpdateNodesResponse {
    children: TreePicklistItem[];
    loading: boolean;
}

export const useUpdateNodes = ({
    props,
    summaryOpened,
    nodesNeedToOpen,
    searchMode,
}: UseUpdateNodesParams): UpdateNodesResponse => {
    const revertShowMoreChildren = useRevertShowMoreChildren(props.item.elementId);

    const onLoadingError = useCallback(() => {
        props.onLoadingError();
        revertShowMoreChildren();
    }, []);

    const useRequestChildrenNodesEffect = createRequestChildrenNodesEffect({
        elementId: props.item.elementId,
        summaryOpened: summaryOpened,
        searchMode: searchMode,
        nodesNeedToOpen: nodesNeedToOpen,
        childrenCount: searchMode ? nodesNeedToOpen.length : Number(props.item.childrenCount),
        childColumnName: props.childColumnName,
        parentColumnName: props.parentColumnName,
    });

    const createTreePicklistItem = useTreePicklistItemFabric(props);

    const { nodeList, loading } = useNodesResponse({
        props: props,
        onLoadingError: onLoadingError,
        useRequestNodesEffect: useRequestChildrenNodesEffect,
    });

    const childrenResponse = nodeList.filter(item => item[props.childColumnName] === props.item.elementId);
    const ancestorsResponse = nodeList.filter(item => item[props.childColumnName] !== props.item.elementId);

    const children = childrenResponse.map(item => createTreePicklistItem(item, ancestorsResponse));

    return { children: children, loading: loading };
};

const createRequestChildrenNodesEffect =
    (params: Omit<RequestChildrenNodesParams, "requestNodes">): UseRequestNodesEffect =>
    requestNodes => {
        useRequestChildrenNodesEffect({ ...params, requestNodes: requestNodes });
    };

interface RequestChildrenNodesParams {
    elementId: string;
    summaryOpened: boolean;
    searchMode: boolean;
    nodesNeedToOpen: TreePicklistItem[];
    childrenCount: number;
    childColumnName: string;
    parentColumnName: string;
    requestNodes: RequestNodesCallback;
}

const useRequestChildrenNodesEffect = (params: RequestChildrenNodesParams) => {
    const visibleChildrenCount = useVisibleChildrenCount(params.elementId);
    const [prevVisibleChildrenCount, setPrevVisibleChildrenCount] = useState(0);

    useAsyncEffect(
        async signal => {
            const summaryClosed = !params.summaryOpened;
            if (summaryClosed) {
                return;
            }

            const childrenCount = Math.min(visibleChildrenCount, params.childrenCount);
            const shouldRequestNewNodes = childrenCount !== prevVisibleChildrenCount;

            if (!shouldRequestNewNodes) {
                return;
            }

            const childrenNodesIds = selectChildrenNodesIds(params.nodesNeedToOpen);
            const filterBuilder = params.searchMode
                ? new FilterBuilder().column(params.parentColumnName).in(childrenNodesIds)
                : new FilterBuilder().column(params.childColumnName).eql(params.elementId);
            const requestChildrenCount = childrenCount - prevVisibleChildrenCount;
            const filterQueryOptions: FilterQueryOptions = {
                nodeFilter: filterBuilder,
                take: requestChildrenCount,
                skip: prevVisibleChildrenCount,
            };

            await params.requestNodes(filterQueryOptions, signal.aborted);
            if (!signal.aborted) {
                setPrevVisibleChildrenCount(childrenCount);
            }
        },
        [params.summaryOpened, visibleChildrenCount]
    );
};

function selectChildrenNodesIds(nodesNeedToOpen: TreePicklistItem[]): string[] {
    return nodesNeedToOpen
        .map(item => item.elementId)
        .filter(isNotNullOrUndefined)
        .map(item => item.toString());
}
